import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, animate } from "framer-motion";
import { ArrowRight, Search } from "lucide-react";
import { COMPANY, COPY, DEALER, FEATURES } from "@/lib/company";
import { api } from "@/lib/api";

const HERO = DEALER.hero;
const ease = [0.16, 1, 0.3, 1];

const useMedia = (query) => {
  const [match, setMatch] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const fn = (e) => setMatch(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, [query]);
  return match;
};

const useSceneRotation = (enabled, videoRefs) => {
  const [scene, setScene] = useState(0);
  useEffect(() => {
    if (!enabled || HERO.videos.length < 2) {
      setScene(0);
      return undefined;
    }
    const t = setInterval(() => {
      setScene((s) => {
        const next = (s + 1) % HERO.videos.length;
        const el = videoRefs.current[next];
        return el && el.readyState >= 2 ? next : s;
      });
    }, HERO.sceneDuration || 8000);
    return () => clearInterval(t);
  }, [enabled, videoRefs]);
  return scene;
};

// Mobile browsers only autoplay when the muted/playsinline attributes exist on the element itself.
const primeVideo = (el) => {
  if (!el) return;
  el.muted = true;
  el.defaultMuted = true;
  ["muted", "playsinline", "webkit-playsinline"].forEach((a) => el.setAttribute(a, ""));
};

const mobileSrc = (src, i) => HERO.mobileVideos?.[i] || src.replace("hd_1920_1080", "sd_960_540");

const GESTURES = ["touchstart", "scroll", "click"];

const useKeepPlaying = (heroRef, videoRefs, active, enabled) => {
  useEffect(() => {
    if (!enabled) return;
    const el = videoRefs.current[active];
    const ensurePlay = () => {
      if (!el || !el.paused) return;
      primeVideo(el);
      el.play().catch(() => {});
    };
    ensurePlay();
    el?.addEventListener("canplay", ensurePlay);
    const io = new IntersectionObserver((es) => es.forEach((e) => e.isIntersecting && ensurePlay()), { threshold: 0.05 });
    if (heroRef.current) io.observe(heroRef.current);
    document.addEventListener("visibilitychange", ensurePlay);
    GESTURES.forEach((g) => window.addEventListener(g, ensurePlay, { passive: true }));
    return () => {
      io.disconnect();
      el?.removeEventListener("canplay", ensurePlay);
      document.removeEventListener("visibilitychange", ensurePlay);
      GESTURES.forEach((g) => window.removeEventListener(g, ensurePlay));
    };
  }, [heroRef, videoRefs, active, enabled]);
};

const useShowroomPs = (enabled) => {
  const [total, setTotal] = useState(null);
  useEffect(() => {
    if (!enabled) return;
    api.get("/showroom").then(({ data }) => setTotal(data.total_ps || 0)).catch(() => setTotal(0));
  }, [enabled]);
  return total;
};

const CountUp = ({ value }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    const controls = animate(0, value, { duration: 2, delay: 0.5, ease, onUpdate: (x) => setN(Math.round(x)) });
    return () => controls.stop();
  }, [value]);
  return <span className="tabular-nums" data-testid="hero-ps-total">{n.toLocaleString("de-DE")}</span>;
};

const HeroTitle = () => {
  const ps = useShowroomPs(Boolean(COPY.heroPsSubtitle));
  if (!COPY.heroPsSubtitle || ps === 0) {
    return <>{COPY.heroTitle}<br /><span className="text-white/60">{COPY.heroSubtitle}</span></>;
  }
  return (
    <>
      Derzeit {ps === null ? <span className="tabular-nums opacity-0">0.000</span> : <CountUp value={ps} />} PS
      <br />
      <span className="text-white/60">{COPY.heroPsSubtitle}</span>
    </>
  );
};

const HeroCopy = () => (
  <>
    <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2, ease }}
      className="text-white/65 text-xs sm:text-sm tracking-[0.35em] uppercase mb-5" data-testid="hero-eyebrow">
      {COPY.heroEyebrow}
    </motion.p>
    <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.35, ease }}
      className="font-display text-white font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl" data-testid="hero-title">
      <HeroTitle />
    </motion.h1>
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.55, ease }}
      className="flex flex-wrap gap-4 mt-10">
      <Link to="/fahrzeuge" data-testid="hero-cta-fahrzeuge"
        className="btn-pill inline-flex items-center gap-2 bg-white text-black font-semibold px-8 py-4 text-sm hover:bg-white/90">
        Fahrzeugbestand <ArrowRight size={16} />
      </Link>
      {FEATURES.findet && (
        <Link to="/fahrzeugsuche" data-testid="hero-cta-findet"
          className="btn-pill inline-flex items-center gap-2 glass-dark text-white font-semibold px-8 py-4 text-sm hover:bg-white/10">
          <Search size={16} /> Wunschfahrzeug finden
        </Link>
      )}
    </motion.div>
    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.8 }}
      className="text-white/45 text-xs mt-8 tracking-wide pr-20 sm:pr-0">{COMPANY.region}{COMPANY.appointmentOnly ? " · Besichtigung nach Terminvereinbarung" : ""}</motion.p>
  </>
);

const Videos = ({ list, scene, videoRefs, small }) =>
  list.map((src, i) => (
    <video key={src} ref={(el) => { primeVideo(el); videoRefs.current[i] = el; }} data-testid={i === 0 ? "hero-video" : `hero-video-${i}`}
      className={`hero-video absolute inset-0 w-full h-full object-cover transition-opacity duration-[1600ms] ease-in-out ${i === scene ? "opacity-100" : "opacity-0"}`}
      src={small ? mobileSrc(src, i) : src} poster={i === 0 ? HERO.poster : undefined} autoPlay muted loop playsInline
      disablePictureInPicture controls={false} preload={i === 0 ? "auto" : "metadata"} aria-hidden="true" />
  ));

export const HeroVideo = () => {
  const heroRef = useRef(null);
  const videoRefs = useRef([]);
  const isDesktop = useMedia("(min-width: 1024px)");
  const reduced = useMedia("(prefers-reduced-motion: reduce)");
  const cinematic = isDesktop && !reduced;
  const scene = useSceneRotation(cinematic, videoRefs);
  useKeepPlaying(heroRef, videoRefs, cinematic ? scene : 0, !reduced);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const contentY = useTransform(scrollYProgress, [0, 0.6], [0, -90]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.45], [1, 0]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.12, 0.75]);

  return (
    <section ref={heroRef} data-testid="hero-section" className={`relative bg-black ${cinematic ? "h-[160vh]" : "h-[100svh]"}`}>
      <div className={`${cinematic ? "sticky top-0" : "relative"} h-[100svh] overflow-hidden`}>
        <motion.div style={cinematic ? { scale: videoScale } : undefined} className="absolute inset-0">
          {reduced ? (
            <img src={HERO.poster} alt="" aria-hidden="true" data-testid="hero-poster" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <Videos list={cinematic ? HERO.videos : HERO.videos.slice(0, 1)} scene={cinematic ? scene : 0} videoRefs={videoRefs} small={!isDesktop} />
          )}
        </motion.div>
        <motion.div style={{ opacity: cinematic ? overlayOpacity : 0.12 }} className="absolute inset-0 bg-black pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/45 pointer-events-none" />
        <motion.div style={cinematic ? { y: contentY, opacity: contentOpacity } : undefined}
          className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 h-full flex flex-col justify-end pb-24 md:pb-32">
          <HeroCopy />
        </motion.div>
        <motion.div style={{ opacity: contentOpacity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-3 text-white/50">
          <span className="text-[10px] tracking-[0.35em] uppercase">Scrollen</span>
          <span className="scroll-indicator" />
        </motion.div>
      </div>
    </section>
  );
};

export default HeroVideo;
